import { v4 as uuidv4 } from 'uuid'
import { useContext, useEffect, useRef, useState } from 'react'
import './BookmarkChat.scss'
import useBookmarks, {
  GeneratingBookmarkStatus,
} from 'containers/embed/useBookmarks'
import ScreenLoading from 'components/ScreenLoading'
import { BookmarksContext } from 'containers/bookmarks/BookmarksContext'
import { Bookmark } from 'types'
import { useHistory } from 'react-router-dom'

type Message = {
  id: string
  status: 'error' | 'info'
  message: string
  type: 'bot' | 'user'
  action?: 'apply'
  bookmark?: Bookmark
}

const BookmarkChat = ({ reportSlug, organizationId }) => {
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [listeningForVoiceInput, setListeningForVoiceInput] = useState(false)
  const [isUtteranceEnabled, setIsUtteranceEnabled] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState('en-US')

  const [messages, setMessages] = useState<Message[]>([])
  const [currentMessage, setCurrentMessage] = useState('')
  const messageInputRef = useRef<HTMLInputElement>(null)
  const {
    generateBookmark,
    generatingBookmarkStatus,
    setGeneratingBookmarkStatus,
    setGenerationResponse,
    bookmarkError,
    setBookmarkError,
  } = useBookmarks(organizationId)
  const { setActiveBookmark, setReportBookmarks } =
    useContext(BookmarksContext)
  const history = useHistory()

  const openChat = () => {
    setIsChatOpen((prev) => !prev)
  }

  const sendMessage = async () => {
    if (currentMessage.length === 0) {
      return
    }

    const message = currentMessage
    setCurrentMessage('')
    setMessages((prev) => [
      ...prev,
      { id: uuidv4(), message: message, type: 'user' } as Message,
    ])

    try {
      const response = await generateBookmark(reportSlug, message)
      if (response?.success) {
        setReportBookmarks((prevBookmarks) => [
          ...prevBookmarks,
          response.bookmark,
        ])
        if (response.text_response && response.text_response.length > 0) {
          const generationDetailsMessage: Message = {
            id: uuidv4(),
            status: 'info',
            type: 'bot',
            message: response.text_response,
          }
          if (isUtteranceEnabled) {
            const utterance = new SpeechSynthesisUtterance(
              response.text_response
            )
            utterance.lang = 'en-US'
            utterance.onend = () => {}
            utterance.onerror = (event) => {
              console.error('Speech synthesis error:', event)
            }
            window.speechSynthesis.speak(utterance)
          }
          setMessages((prev) => [...prev, generationDetailsMessage])
          setGenerationResponse(null)
        }
        const applyMessage: Message = {
          id: uuidv4(),
          status: 'info',
          type: 'bot',
          message: `Successfully created bookmark '${response.bookmark.name}' with prompt '${message}'.`,
          action: 'apply',
          bookmark: response.bookmark,
        }
        setMessages((prev) => [...prev, applyMessage])
      }
    } catch (e) {
      console.log(e)
      const errorMessage: Message = {
        id: uuidv4(),
        status: 'error',
        type: 'bot',
        message: 'Oh oh, seems like something went wrong.',
      }
      setMessages((prev) => [...prev, errorMessage])
    }
  }

  const applyBookmark = async (bookmark: Bookmark) => {
    setActiveBookmark(bookmark)

    const appliedMessage: Message = {
      id: uuidv4(),
      status: 'info',
      type: 'bot',
      message: `Successfully applied bookmark '${bookmark?.name}'`,
    }
    setMessages((prev) => [...prev, appliedMessage])

    const currentOrg = localStorage.getItem('currentOrganization')
    history.push(
      `/organization/${currentOrg}/dashboard/${reportSlug}/bookmarks/${bookmark?.slug}`
    )
  }

  const clearHistory = () => {
    setMessages([])
    setGeneratingBookmarkStatus(GeneratingBookmarkStatus.idle)
    sessionStorage.removeItem(`${organizationId}-${reportSlug}-bookmarks`)
  }

  const getVoiceInput = () => {
    if (listeningForVoiceInput) {
      setListeningForVoiceInput(false)
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel()
      }
      const infoMessage: Message = {
        id: uuidv4(),
        status: 'info',
        type: 'bot',
        message: 'Stopped listening for voice input.',
      }
      setMessages((prev) => [...prev, infoMessage])
      return
    }

    if (window.speechSynthesis) {
      // -- Concfig
      const recognition = new (window as any).webkitSpeechRecognition()
      if (selectedLanguage) {
        recognition.lang = selectedLanguage
      } else {
        recognition.lang = 'en-US'
      }
      recognition.interimResults = true
      recognition.maxAlternatives = 1
      // if the user hasn't spoken for 1 second  stop recognition
      recognition.continuous = false
      
      // -- Events
      recognition.onstart = () => {
        setListeningForVoiceInput(true)
        const infoMessage: Message = {
          id: uuidv4(),
          status: 'info',
          type: 'bot',
          message: 'Listening for your voice input...',
        }
        setMessages((prev) => [...prev, infoMessage])
      }
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript
        setCurrentMessage(transcript)
        setListeningForVoiceInput(false)
      }

      recognition.onerror = (event) => {
        console.error('Speech recognition error detected:', event.error)
        let message = ''
        switch (event.error) {
          case 'aborted':
            message = 'Speech recognition was aborted.'
            const infoMessage: Message = {
              id: uuidv4(),
              status: 'info',
              type: 'bot',
              message: 'Speech recognition was aborted. Please try again in a bit.',
            }
            setMessages((prev) => [...prev, infoMessage])
            break
          default:
            const errorMessage: Message = {
              id: uuidv4(),
              status: 'error',
              type: 'bot',
              message: `Error occurred in recognition: ${message}`,
            } 
            setMessages((prev) => [...prev, errorMessage])
        }
        setListeningForVoiceInput(false)
      }

      recognition.onend = () => {
        console.log('Speech recognition service disconnected')
      }

      recognition.start()
    } else {
      const errorMessage: Message = {
        id: uuidv4(),
        status: 'error',
        type: 'bot',
        message: 'Speech recognition is not supported in this browser.',
      }
      setMessages((prev) => [...prev, errorMessage])
      setListeningForVoiceInput(false)
    }
  }

  useEffect(() => {
    const enterListener = (e) => {
      if (e.key === 'Enter') {
        sendMessage()
      }
    }

    if (messageInputRef.current !== null) {
      messageInputRef.current.addEventListener('keydown', enterListener)
    }

    return () => {
      messageInputRef.current?.removeEventListener('keydown', enterListener)
    }
  }, [messageInputRef, currentMessage])

  useEffect(() => {
    document.getElementById(messages[messages.length - 1]?.id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    })

    if (messages.length > 0) {
      sessionStorage.setItem(
        `${organizationId}-${reportSlug}-bookmarks`,
        JSON.stringify(messages)
      )
    }
  }, [messages, isChatOpen])

  useEffect(() => {
    if (bookmarkError) {
      const errorMessage: Message = {
        id: uuidv4(),
        status: 'error',
        type: 'bot',
        message: `Oh oh, seems like something went wrong\n\nError:\n${
          bookmarkError.message
        }\n\nDetail:\n${bookmarkError.type}${
          bookmarkError?.tip ? `\n\nTip:\n${bookmarkError.tip}` : ''
        }`,
      }
      setMessages((prev) => [...prev, errorMessage])
      setBookmarkError(null)
    }
  }, [bookmarkError])

  useEffect(() => {
    const storedHistory = sessionStorage.getItem(
      `${organizationId}-${reportSlug}-bookmarks`
    )
    if (storedHistory) {
      const parsedHistory = JSON.parse(storedHistory)
      setMessages(parsedHistory)
      setGeneratingBookmarkStatus(GeneratingBookmarkStatus.idle)
    }
  }, [])

  return (
    <div className="chat">
      <div
        className={`bubble ${!isChatOpen ? 'tooltip' : ''} top md`}
        onClick={openChat}
        data-tooltip="AI Builder"
      >
        <span className="material-icons-outlined non-selectable">
          {isChatOpen ? 'assistant' : 'assistant'} 
        </span>
      </div>
      {isChatOpen && (
        <>
          <div className={`chat-body ${isFullscreen ? 'fullscreen' : ''}`}>
            <div className="chat-messages">
              <div className="message bot">
                <p className="bot">Hello! Write a prompt to get started</p>
              </div>
              {messages.map((message) => {
                return (
                  <div
                    key={message.id}
                    id={message.id}
                    className={`message ${message.type}`}
                  >
                    <p className={`${message.type} ${message.status}`}>
                      {message.message}
                    </p>
                    {message.action === 'apply' && (
                      <div
                        className="message-action"
                        onClick={() => applyBookmark(message.bookmark!)}
                      >
                        Apply
                      </div>
                    )}
                  </div>
                )
              })}
              {generatingBookmarkStatus ===
                GeneratingBookmarkStatus.loading && (
                <ScreenLoading></ScreenLoading>
              )}
            </div>
            <div className="chat-bottom">
              <div className="chat-header">
                <h3>Message</h3>
                <div className="chat-header-actions">
                  <button onClick={clearHistory} className="clear">
                    Clear
                  </button>
                  {/* Speech reco */}
                  {/* <button onClick={() => setIsUtteranceEnabled((prev) => !prev)} className="tts">
                    <span className="material-icons-outlined non-selectable">
                      {isUtteranceEnabled ? "record_voice_over" : 'voice_over_off'}
                    </span>
                  </button>
                  <div className="select-wrapper">
                      <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="language-select"
                      >
                        <option value="en-US">English (US)</option>
                        <option value="el-GR">Greek</option>
                        <option value="es-ES">Spanish</option>
                        <option value="fr-FR">French</option>
                        <option value="de-DE">German</option>
                        <option value="it-IT">Italian</option>
                        <option value="zh-CN">Chinese (Simplified)</option>
                        <option value="ja-JP">Japanese</option>
                      </select>
                  </div> */}
                  <button
                    onClick={() => setIsFullscreen((prev) => !prev)}
                    className="expand"
                  >
                    <span className="material-icons-outlined non-selectable">
                      {!isFullscreen ? 'open_in_full' : 'close_fullscreen'}
                    </span>
                  </button>
                </div>
              </div>
              <div className="chat-input">
                <input
                  ref={messageInputRef}
                  placeholder="Type your desired visual"
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                />
                {/* Speech reco */}
                {/* <button
                  className={`listen-button ${
                    listeningForVoiceInput ? 'active' : ''
                  } tooltip top`}
                  onClick={getVoiceInput}
                  data-tooltip="Speak"
                >
                  <span className="material-icons-outlined non-selectable">
                    {listeningForVoiceInput ? 'mic_off' : 'mic'}
                  </span>
                </button> */}
                <button
                  className={`send-message ${
                    currentMessage.length === 0 ? 'disabled' : 'tooltip top'
                  }`}
                  onClick={sendMessage}
                  disabled={currentMessage.length === 0}
                  data-tooltip="Send"
                >
                  <span className="material-icons-outlined non-selectable">
                    send
                  </span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default BookmarkChat
